import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, tap } from 'rxjs';

export interface ProjectSettings {
    logoUrl: string | null;
    backgroundUrl: string | null;
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    staffLabel: string;
    customerLabel: string;
    rolesEnabled?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class SettingsService {
    public settingsSubject = new BehaviorSubject<ProjectSettings>({
        logoUrl: null,
        backgroundUrl: null,
        primaryColor: '#3b82f6',
        secondaryColor: '#1f2937',
        accentColor: '#f59e0b',
        staffLabel: 'Staff',
        customerLabel: 'Customers',
        rolesEnabled: false
    });

    public settings$ = this.settingsSubject.asObservable();

    constructor(
        private http: HttpClient,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {
        // Don't load during SSR — would trigger localhost SSRF block in Angular 21+
        if (isPlatformBrowser(this.platformId)) {
            this.loadSettings();
        }
    }

    loadSettings() {
        if (!isPlatformBrowser(this.platformId)) return;
        this.http.get<ProjectSettings>('/api/settings').subscribe({
            next: (settings) => {
                if (settings) {
                    this.settingsSubject.next(settings);
                    this.applyPalette(settings);
                }
            },
            error: (err) => {
                console.error('[SettingsService] Failed to load settings:', err);
                // Non propaghiamo l'errore per non far crashare l'app
            }
        });
    }

    updateSettings(settings: ProjectSettings) {
        return this.http.post('/api/settings', settings).pipe(
            tap(() => {
                this.settingsSubject.next(settings);
                this.applyPalette(settings);
            })
        );
    }

    // Live preview without saving
    previewSettings(settings: ProjectSettings) {
        this.applyPalette(settings);
    }

    // Reset to last saved state
    cancelChanges() {
        this.applyPalette(this.settingsSubject.value);
    }

    private applyPalette(settings: ProjectSettings) {
        if (isPlatformBrowser(this.platformId)) {
            const root = document.documentElement;

            const applyColor = (name: string, hex: string) => {
                const v = this.hexToVariants(hex);
                root.style.setProperty(`--${name}`, hex);
                root.style.setProperty(`--${name}-dark`, v.dark);
                root.style.setProperty(`--${name}-light`, v.light);
                root.style.setProperty(`--${name}-rgb`, v.rgb);
            };

            applyColor('primary-color', settings.primaryColor);
            applyColor('secondary-color', settings.secondaryColor);
            applyColor('accent-color', settings.accentColor);

            if (settings.backgroundUrl) {
                root.style.setProperty('--bg-image', `url(${settings.backgroundUrl})`);
            } else {
                root.style.setProperty('--bg-image', 'none');
            }
        }
    }

    /** Calcola varianti dark (-15% L), light (+30% L) e RGB string da un colore hex. */
    private hexToVariants(hex: string): { dark: string; light: string; rgb: string } {
        // Normalizza hex → r,g,b 0-255
        const clean = hex.replace('#', '');
        const r = parseInt(clean.slice(0, 2), 16);
        const g = parseInt(clean.slice(2, 4), 16);
        const b = parseInt(clean.slice(4, 6), 16);

        if (isNaN(r) || isNaN(g) || isNaN(b)) {
            return { dark: hex, light: hex, rgb: '0,0,0' };
        }

        const rgb = `${r},${g},${b}`;

        // Converti in HSL
        const rn = r / 255, gn = g / 255, bn = b / 255;
        const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
        let h = 0, s = 0;
        const l = (max + min) / 2;

        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case rn: h = (gn - bn) / d + (gn < bn ? 6 : 0); break;
                case gn: h = (bn - rn) / d + 2; break;
                case bn: h = (rn - gn) / d + 4; break;
            }
            h /= 6;
        }

        const hDeg = Math.round(h * 360);
        const sPct = Math.round(s * 100);
        const lPct = Math.round(l * 100);

        // Dark = -15 L (min 5%), Light = +30 L (max 95%)
        const darkL = Math.max(5, lPct - 15);
        const lightL = Math.min(95, lPct + 30);

        return {
            dark: `hsl(${hDeg}, ${sPct}%, ${darkL}%)`,
            light: `hsl(${hDeg}, ${sPct}%, ${lightL}%)`,
            rgb,
        };
    }


    uploadAsset(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        // Using 'default' as projectId for now as it's a single project dashboard
        return this.http.post<{ data: string[] }>('/api/assets/default', formData);
    }

    deleteAsset(url: string) {
        // Extract filename from URL: /api/assets/default/file/filename
        const parts = url.split('/file/');
        if (parts.length < 2) return this.http.delete('/api/assets/default/invalid');

        const filename = parts[1];
        return this.http.delete(`/api/assets/default/${filename}`);
    }

    listAssets() {
        return this.http.get<Array<{ src: string; name: string }>>('/api/assets/default');
    }
}
